import React, { useEffect, useMemo, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';

import Yup from '../../../utils/yup';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { BiWritebackInputType } from '../../../config/bi_report';
import {
  ButtonUI,
  CheckboxUI,
  DatePickerUI,
  DateTimePickerUI,
  DialogUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
} from '../../Interface';
import biWritebackService from '../../../service/biWriteback.service';

const getValidationForField = (field) => {
  let validation;

  switch (field.inputType) {
    case BiWritebackInputType.INTEGER:
      validation = Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .integer()
        .nullable();
      break;
    case BiWritebackInputType.DECIMAL:
      validation = Yup.number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .nullable();
      break;
    case BiWritebackInputType.DATE:
    case BiWritebackInputType.DATETIME:
      validation = Yup.mixed()
        .nullable()
        .test('valid-date', 'Érvénytelen dátum!', (value) => value === null || value === '' || dayjs(value).isValid());
      break;
    case BiWritebackInputType.BOOLEAN:
      validation = Yup.boolean().nullable();
      break;
    default:
      validation = Yup.string().nullable();
      break;
  }

  return field.required ? validation.required('Kötelező!') : validation;
};

const serializeValue = (field, value) => {
  if (value === '' || value === undefined) return null;
  if (value === null) return null;

  if (field.inputType === BiWritebackInputType.DATE) {
    return dayjs(value).format('YYYY-MM-DD');
  }

  if (field.inputType === BiWritebackInputType.DATETIME) {
    return dayjs(value).toISOString();
  }

  if (field.inputType === BiWritebackInputType.INTEGER) return Number.parseInt(value, 10);
  if (field.inputType === BiWritebackInputType.DECIMAL) return Number(value);

  return value;
};

const getInitialValue = (field, value) => {
  if (field.inputType === BiWritebackInputType.BOOLEAN) return Boolean(value);
  if (field.inputType === BiWritebackInputType.DATE || field.inputType === BiWritebackInputType.DATETIME) {
    return value ? dayjs(value) : null;
  }
  return value ?? '';
};

const WritebackInput = ({ field, name, value, error, setFieldValue }) => {
  const label = <FormLabelUI text={field.label} />;
  const helperText = <FormErrorUI message={error} />;

  if (field.inputType === BiWritebackInputType.BOOLEAN) {
    return (
      <Field
        name={name}
        as={CheckboxUI}
        label={label}
        checked={Boolean(value)}
        onChange={(event) => setFieldValue(name, event.target.checked)}
      />
    );
  }

  if (field.inputType === BiWritebackInputType.DATE) {
    return (
      <DatePickerUI
        name={name}
        label={label}
        value={value}
        helperText={helperText}
        onChange={(newValue) => setFieldValue(name, newValue)}
      />
    );
  }

  if (field.inputType === BiWritebackInputType.DATETIME) {
    return (
      <DateTimePickerUI
        name={name}
        label={label}
        value={value}
        helperText={helperText}
        onChange={(newValue) => setFieldValue(name, newValue)}
      />
    );
  }

  return (
    <Field
      name={name}
      as={InputUI}
      type={
        field.inputType === BiWritebackInputType.INTEGER || field.inputType === BiWritebackInputType.DECIMAL
          ? 'number'
          : 'text'
      }
      fullWidth
      label={label}
      variant="standard"
      helperText={helperText}
      multiline={field.inputType === BiWritebackInputType.TEXTAREA}
      rows={field.inputType === BiWritebackInputType.TEXTAREA ? 3 : undefined}
      InputLabelProps={{ shrink: value !== '' && value !== null }}
    />
  );
};

const WritebackDialog = ({ open, onHide, row, writebacks, onSaved }) => {
  const [formData, setFormData] = useState({ writebackValues: {} });
  const [writebackKeys, setWritebackKeys] = useState({});
  const [loading, setLoading] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        writebackValues: Yup.object().shape(
          Object.fromEntries(
            (writebacks ?? []).map((writeback) => [
              writeback.id,
              Yup.object().shape(
                Object.fromEntries(writeback.fields.map((field) => [field.id, getValidationForField(field)]))
              ),
            ])
          )
        ),
      }),
    [writebacks]
  );

  useEffect(() => {
    if (!open || !writebacks?.length || !row) return;

    const missingKey = writebacks
      .flatMap((writeback) => writeback.keys)
      .find((key) => !Object.prototype.hasOwnProperty.call(row, key.sourceField));

    if (missingKey) {
      addNotification({
        content: `Hiányzó kulcsmező a riportból: ${missingKey.sourceField}`,
        type: NotificationType.WARNING,
      });
      onHide();
      return;
    }

    const selectedKeys = Object.fromEntries(
      writebacks.map((writeback) => [
        writeback.id,
        writeback.keys.map((key) => ({
          keyId: key.id,
          value: row[key.sourceField],
        })),
      ])
    );
    const fallbackValues = Object.fromEntries(
      writebacks.map((writeback) => [
        writeback.id,
        Object.fromEntries(
          writeback.fields.map((field) => [field.id, getInitialValue(field, row[field.sourceField])])
        ),
      ])
    );

    setWritebackKeys(selectedKeys);
    setFormData({ writebackValues: fallbackValues });
    setLoading(true);

    let isCurrentRequest = true;
    Promise.all(
      writebacks.map((writeback) =>
        biWritebackService
          .getCurrentValues(writeback.id, { keys: selectedKeys[writeback.id] })
          .then((data) => ({ writeback, data }))
      )
    )
      .then((results) => {
        if (!isCurrentRequest) return;
        const currentValues = Object.fromEntries(
          results.map(({ writeback, data }) => {
            const values = Object.fromEntries(data.fields.map((field) => [field.fieldId, field.value]));
            return [
              writeback.id,
              Object.fromEntries(
                writeback.fields.map((field) => [field.id, getInitialValue(field, values[field.id])])
              ),
            ];
          })
        );
        setFormData({
          writebackValues: currentValues,
        });
      })
      .finally(() => {
        if (isCurrentRequest) setLoading(false);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [open, onHide, row, writebacks]);

  const handleSave = (values) => {
    setLoading(true);
    Promise.all(
      writebacks.map((writeback) => {
        const fields = writeback.fields.map((field) => ({
          fieldId: field.id,
          value: serializeValue(field, values.writebackValues[writeback.id][field.id]),
        }));

        return biWritebackService.updateValues(writeback.id, {
          keys: writebackKeys[writeback.id],
          fields,
        });
      })
    )
      .then(() => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        onSaved();
        onHide();
      })
      .finally(() => setLoading(false));
  };

  return (
    <DialogUI open={open} headerContent="Szerkesztés" fullWidth={true} maxWidth="md">
      <Formik
        initialValues={formData}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSave}
      >
        {({ errors, values, setFieldValue }) => (
          <Form>
            <div className="grid grid-cols-1 gap-4 px-5 py-4">
              {loading ? (
                <div className="flex min-h-[120px] items-center justify-center">
                  <CircularProgress />
                </div>
              ) : (
                writebacks?.map((writeback) => (
                  <div className="rounded border border-gray-300 p-4" key={writeback.id}>
                    <div className="mb-4 text-lg font-semibold">{writeback.name}</div>
                    <div className="grid grid-cols-1 gap-5">
                      {writeback.fields.map((field) => {
                        const name = `writebackValues.${writeback.id}.${field.id}`;
                        return (
                          <WritebackInput
                            key={field.id}
                            field={field}
                            name={name}
                            value={values.writebackValues[writeback.id]?.[field.id]}
                            error={errors.writebackValues?.[writeback.id]?.[field.id]}
                            setFieldValue={setFieldValue}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="flex justify-center gap-3 px-4 pb-5">
              <ButtonUI text="Mentés" className="bg-success" type="submit" disabled={loading} />
              <ButtonUI text="Mégse" color="blue-gray" type="button" variant="gradient" onClick={onHide} />
            </div>
          </Form>
        )}
      </Formik>
    </DialogUI>
  );
};

export default WritebackDialog;
